import { CmsPage, SEO_DESCRIPTION_LIMIT, SEO_DESCRIPTION_MIN, SEO_TITLE_LIMIT } from "services/cms-seo/type"

export type SeoCheckLevel = "ok" | "warn" | "bad"

export interface SeoCheck {
  id: string
  level: SeoCheckLevel
  message: string
}

export interface SeoScoreResult {
  passed: number
  total: number
  checks: SeoCheck[]
}

const MIN_INTERNAL_LINKS = 3
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const parseBody = (html: string): Document | null => {
  if (typeof DOMParser === "undefined") return null
  try {
    return new DOMParser().parseFromString(`<div>${html}</div>`, "text/html")
  } catch {
    return null
  }
}

const titleCheck = (page: CmsPage): SeoCheck => {
  const length = page.seo_title.trim().length
  if (length === 0) return { id: "title", level: "bad", message: "Title tag is empty" }
  if (length > SEO_TITLE_LIMIT) {
    return { id: "title", level: "warn", message: `Title tag is ${length} characters — Google truncates past ${SEO_TITLE_LIMIT}` }
  }
  return { id: "title", level: "ok", message: `Title tag ${length} characters` }
}

const descriptionCheck = (page: CmsPage): SeoCheck => {
  const length = page.seo_description.trim().length
  if (length === 0) return { id: "description", level: "bad", message: "Meta description is empty" }
  if (length > SEO_DESCRIPTION_LIMIT) {
    return {
      id: "description",
      level: "warn",
      message: `Description is ${length} characters — Google truncates past ${SEO_DESCRIPTION_LIMIT}`
    }
  }
  if (length < SEO_DESCRIPTION_MIN) {
    return { id: "description", level: "warn", message: `Description is only ${length} characters — aim for ${SEO_DESCRIPTION_MIN}+` }
  }
  return { id: "description", level: "ok", message: `Meta description ${length} characters` }
}

const headingCheck = (page: CmsPage, body: Document | null): SeoCheck => {
  const bodyH1Count = body ? body.querySelectorAll("h1").length : 0
  const total = (page.h1.trim() ? 1 : 0) + bodyH1Count
  if (total === 0) return { id: "h1", level: "bad", message: "No H1 on the page" }
  if (total > 1) return { id: "h1", level: "bad", message: `${total} H1 headings — a page must have exactly one` }
  return { id: "h1", level: "ok", message: "Exactly one H1" }
}

const headingDiffersCheck = (page: CmsPage): SeoCheck => {
  const h1 = page.h1.trim().toLowerCase()
  const title = page.seo_title.trim().toLowerCase()
  if (!h1 || !title) return { id: "h1-differs", level: "warn", message: "H1 and title tag cannot be compared while either is empty" }
  if (h1 === title) return { id: "h1-differs", level: "warn", message: "H1 repeats the title tag verbatim" }
  return { id: "h1-differs", level: "ok", message: "H1 differs from title tag" }
}

const canonicalCheck = (page: CmsPage): SeoCheck => {
  if (!page.canonical) return { id: "canonical", level: "ok", message: "Canonical resolves to an indexable URL" }
  let parsed: URL
  try {
    parsed = new URL(page.canonical)
  } catch {
    return { id: "canonical", level: "bad", message: "Canonical URL is not absolute" }
  }
  if (parsed.protocol !== "https:") return { id: "canonical", level: "bad", message: "Canonical URL is not https" }
  if (!page.robots_index) return { id: "canonical", level: "bad", message: "Canonical points at a page marked noindex" }
  return { id: "canonical", level: "ok", message: "Canonical resolves to an indexable URL" }
}

const altTextCheck = (body: Document | null): SeoCheck => {
  if (!body) return { id: "alt", level: "ok", message: "No images without alt text" }
  const images = Array.from(body.querySelectorAll("img"))
  const missing = images.filter((img) => !img.getAttribute("alt")?.trim()).length
  if (missing === 1) return { id: "alt", level: "bad", message: "One image has no alt text" }
  if (missing > 1) return { id: "alt", level: "bad", message: `${missing} images have no alt text` }
  return { id: "alt", level: "ok", message: images.length ? `All ${images.length} images have alt text` : "No images in body" }
}

const internalLinkCheck = (body: Document | null): SeoCheck => {
  if (!body) return { id: "links", level: "warn", message: "No internal links in body" }
  const internal = Array.from(body.querySelectorAll("a[href]")).filter((a) => (a.getAttribute("href") ?? "").startsWith("/")).length
  if (internal < MIN_INTERNAL_LINKS) {
    return { id: "links", level: "warn", message: `${internal} internal links in body — aim for ${MIN_INTERNAL_LINKS}+` }
  }
  return { id: "links", level: "ok", message: `${internal} internal links in body` }
}

const slugCheck = (page: CmsPage): SeoCheck => {
  if (page.path === "/") return { id: "slug", level: "ok", message: "Slug is URL-safe" }
  if (!SLUG_PATTERN.test(page.slug)) {
    return { id: "slug", level: "bad", message: "Slug must be lowercase words joined by single hyphens" }
  }
  return { id: "slug", level: "ok", message: "Slug is URL-safe" }
}

const sitemapCheck = (page: CmsPage): SeoCheck => {
  if (page.sitemap_include === "yes" && !page.robots_index) {
    return { id: "sitemap", level: "bad", message: "Page is noindex but forced into the sitemap" }
  }
  if (page.sitemap_include === "no" && page.robots_index) {
    return { id: "sitemap", level: "warn", message: "Indexable page is excluded from the sitemap" }
  }
  return { id: "sitemap", level: "ok", message: "Indexing and sitemap inclusion agree" }
}

/** The same nine checks the list's score column reports, so the two cannot disagree. */
export const computeSeoScore = (page: CmsPage): SeoScoreResult => {
  const body = parseBody(page.body_html || "")

  const checks: SeoCheck[] = [
    titleCheck(page),
    descriptionCheck(page),
    headingCheck(page, body),
    headingDiffersCheck(page),
    canonicalCheck(page),
    altTextCheck(body),
    internalLinkCheck(body),
    slugCheck(page),
    sitemapCheck(page)
  ]

  return {
    passed: checks.filter((check) => check.level === "ok").length,
    total: checks.length,
    checks
  }
}

export const scoreLevel = (passed: number, total: number): SeoCheckLevel => {
  if (total === 0) return "bad"
  const ratio = passed / total
  if (ratio >= 0.85) return "ok"
  if (ratio >= 0.6) return "warn"
  return "bad"
}
